import { fromTheme, styled, View, webOnlyStyles } from 'lib_ui-primitives';
import PropTypes from 'prop-types';
import { Fragment, createElement as rc, useCallback, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import {
    getGradientStyles,
    getTabBarMinHeight,
    useGradientVisibility,
    useHoverScroll
} from '../layout/TabViewLayout/TabCarousel.gl2025.shared';
import RouteContainer from '../RouteContainer';

const StyledTabNav = styled(View).attrs({ name: 'styled-tab-nav-gl2025' })`
    width: 100%;
    height: 100%;
    flex-direction: column;
    background-color: ${fromTheme('backgroundColor')};
`;

// Tab bar positioned container with GL2025 theme
let TabBar = styled(View).attrs({ name: 'tab-bar-gl2025' })`
    position: relative;
    background-color: ${fromTheme('tabs', 'container', 'backgroundColor')};
    padding-top: ${fromTheme('tabs', 'container', 'paddingTop')};
    border-bottom-width: ${fromTheme('tabs', 'container', 'borderBottomWidth')};
    border-bottom-color: ${fromTheme('tabs', 'container', 'borderBottomColor')};
    margin-bottom: ${fromTheme('tabs', 'container', 'marginBottom')};
    box-shadow: ${fromTheme('tabs', 'container', 'boxShadow')};
`;

TabBar = webOnlyStyles(TabBar)`
    border-bottom-style: solid;
`;

TabBar.displayName = 'TabBar';

// Horizontal scroll container - hides scrollbar, allows smooth scrolling
let ScrollContainer = styled(View).attrs({ name: 'tab-nav-scroll-gl2025' })`
    flex-grow: 1;
    overflow-x: auto;
    overflow-y: hidden;
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    position: relative;

    /* Hide scrollbar */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
    &::-webkit-scrollbar {
        display: none; /* Chrome/Safari */
    }

    /* Smooth scrolling */
    scroll-behavior: smooth;
`;

ScrollContainer = webOnlyStyles(ScrollContainer)`
    -webkit-overflow-scrolling: touch;
`;

// Inner container for tab children with spacing and flex-row layout
const TabsRow = styled(View).attrs({ name: 'tabs-row-gl2025', role: 'tablist' })`
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: ${fromTheme('tabs', 'spacing', 'default')};
    padding-left: ${fromTheme('tabs', 'container', 'paddingLeft')};
    min-width: min-content;
`;

// Empty space to the right of tabs with bottom border
let EmptyTabSpace = styled(View).attrs({ name: 'empty-tab-space-gl2025' })`
    border-bottom-width: ${fromTheme('tabs', 'container', 'borderBottomWidth')};
    border-bottom-color: ${fromTheme('tabs', 'container', 'borderBottomColor')};
    flex-grow: 1;
    flex-basis: 1px;
    height: ${fromTheme('tabs', 'container', 'emptySpaceHeight')};
`;

EmptyTabSpace = webOnlyStyles(EmptyTabSpace)`
    border-bottom-style: solid;
`;

// Gradient overlay - left side
const LeftGradient = styled(View).attrs({ name: 'left-gradient-gl2025' })`
    position: absolute;
    left: 0;
    top: 0;
    width: ${({ gradientStyle }) => gradientStyle.width}px;
    height: ${fromTheme('tabs', 'gradient', 'overlayHeight')};
    background: ${({ gradientStyle }) => gradientStyle.background};
    pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
    display: ${({ visible }) => (visible ? 'block' : 'none')};
    z-index: 10;
    cursor: pointer;
    opacity: ${({ isHovering }) => (isHovering ? 0.9 : 1)};
    transition: opacity 0.15s ease-in-out;
`;

// Gradient overlay - right side
const RightGradient = styled(View).attrs({ name: 'right-gradient-gl2025' })`
    position: absolute;
    right: 0;
    top: 0;
    width: ${({ gradientStyle }) => gradientStyle.width}px;
    height: ${fromTheme('tabs', 'gradient', 'overlayHeight')};
    background: ${({ gradientStyle }) => gradientStyle.background};
    pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
    display: ${({ visible }) => (visible ? 'block' : 'none')};
    z-index: 10;
    cursor: pointer;
    opacity: ${({ isHovering }) => (isHovering ? 0.9 : 1)};
    transition: opacity 0.15s ease-in-out;
`;

/**
 * GL2025-styled navigation container with scrollable tab bar and gradient overlays.
 * Tabs on top (provided as children), content underneath via RouteContainer.
 */
function TabNavGL2025(props) {
    const { children, hNode, currentRoute } = props;
    const theme = useTheme();
    const scrollContainerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const [contentWidth, setContentWidth] = useState(0);

    // Gradient visibility based on scroll position
    const [gradientState, handleScroll] = useGradientVisibility(containerWidth, contentWidth);

    // Ref callback to measure scroll container
    const scrollContainerRefCallback = useCallback(
        node => {
            scrollContainerRef.current = node;
            if (node) {
                const observer = new ResizeObserver(entries => {
                    for (const entry of entries) {
                        const width = entry.contentRect.width;
                        if (width > 0) {
                            setContainerWidth(width);
                            setContentWidth(node.scrollWidth);
                        }
                    }
                });
                observer.observe(node);
                return () => observer.disconnect();
            }
        },
        []
    );

    // Handle scroll events to update gradient visibility
    const onScroll = useCallback(
        e => {
            const { scrollLeft, scrollWidth, clientWidth } = e.target;
            handleScroll(scrollLeft, scrollWidth, clientWidth);
        },
        [handleScroll]
    );

    // Handle mouse wheel for horizontal scrolling on desktop
    const handleWheel = useCallback(e => {
        const container = scrollContainerRef.current;
        if (container && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
            e.preventDefault();
            container.scrollLeft += e.deltaY;
        }
    }, []);

    // Gradient styles from theme
    const leftGradientStyle = getGradientStyles(theme, 'left');
    const rightGradientStyle = getGradientStyles(theme, 'right');

    // Hover-to-scroll on gradient overlays
    const {
        isHoveringLeft,
        isHoveringRight,
        handleLeftMouseEnter,
        handleLeftMouseLeave,
        handleRightMouseEnter,
        handleRightMouseLeave
    } = useHoverScroll(scrollContainerRef);

    // prettier-ignore
    return rc(StyledTabNav, { id: hNode.id },
        rc(TabBar, { style: { minHeight: getTabBarMinHeight(theme) } },
            rc(Fragment, null,
                rc(ScrollContainer, {
                    ref: scrollContainerRefCallback,
                    onScroll,
                    onWheel: handleWheel
                },
                    rc(TabsRow, null,
                        children,
                        rc(EmptyTabSpace, { theme })
                    )
                ),
                rc(LeftGradient, {
                    visible: gradientState.showLeftGradient,
                    gradientStyle: leftGradientStyle,
                    theme,
                    isHovering: isHoveringLeft,
                    onMouseEnter: handleLeftMouseEnter,
                    onMouseLeave: handleLeftMouseLeave
                }),
                rc(RightGradient, {
                    visible: gradientState.showRightGradient,
                    gradientStyle: rightGradientStyle,
                    theme,
                    isHovering: isHoveringRight,
                    onMouseEnter: handleRightMouseEnter,
                    onMouseLeave: handleRightMouseLeave
                })
            )
        ),
        rc(RouteContainer, {
            hNode,
            currentRoute
        })
    );
}

TabNavGL2025.propTypes = {
    hNode: PropTypes.object,
    children: PropTypes.array,
    currentRoute: PropTypes.string
};

export default TabNavGL2025;
